export const PROJECT_ROLE_LABELS = Object.freeze({
  owner: "Titular",
  admin: "Administrador",
  administrativo: "Administrativo",
  cajero: "Cajero/a",
  cobrador: "Cobrador",
  facturacion: "Facturación",
  vendedor: "Vendedor",
  deposito: "Depósito",
  viewer: "Solo lectura",
  superadmin: "Superadministrador",
});

export const PROJECT_STATUS_LABELS = Object.freeze({
  active: "Activo",
  suspended: "Suspendido",
});

export const FINANCIAL_VISIBILITY_LABELS = Object.freeze({
  none: "Sin datos financieros",
  operational: "Operación asignada, sin totales globales",
  full: "Totales completos",
});

export const PERMISSION_GROUPS = Object.freeze([
  {
    key: "project",
    label: "Proyecto y equipo",
    permissions: [
      ["project.view", "Ver proyecto"],
      ["project.settings.manage", "Editar configuración"],
      ["project.members.manage", "Administrar miembros"],
    ],
  },
  {
    key: "customers",
    label: "Clientes y precios",
    permissions: [
      ["customers.read", "Ver clientes registrados"],
      ["customers.manage", "Crear y gestionar clientes"],
      ["customers.pricing.manage", "Asignar listas de precios"],
      ["price_lists.read", "Ver listas de precios"],
      ["price_lists.manage", "Administrar listas de precios"],
    ],
  },
  {
    key: "memberships",
    label: "Membresías",
    capability: "memberships",
    permissions: [
      ["memberships.manage", "Asignar, renovar, pausar y cancelar membresías"],
      ["memberships.payments.create", "Registrar cobros de membresías"],
    ],
  },
  {
    key: "tables",
    label: "Mesas y reservas",
    permissions: [
      ["tables.read", "Ver mesas"],
      ["tables.manage", "Administrar mesas"],
    ],
  },
  {
    key: "real-estate",
    label: "Inmobiliaria",
    permissions: [
      ["properties.read", "Ver propiedades"],
      ["properties.manage", "Crear y gestionar propiedades"],
      ["leases.read", "Ver contratos y alquileres"],
      ["leases.manage", "Crear y gestionar contratos y alquileres"],
      ["lease_collections.read", "Ver cobranzas de alquileres"],
      ["lease_collections.manage", "Registrar y gestionar cobranzas de alquileres"],
    ],
  },
  {
    key: "notifications",
    label: "Notificaciones",
    permissions: [
      ["notifications.send", "Enviar notificaciones del proyecto"],
    ],
  },
  {
    key: "items",
    label: "Ítems del negocio",
    permissions: [
      ["items.read", "Ver ítems"],
      ["items.create", "Crear ítems"],
      ["items.update", "Editar ítems"],
      ["items.delete", "Eliminar ítems"],
      ["items.costs.read", "Ver costos"],
      ["items.costs.manage", "Editar costos"],
      ["items.accounting.manage", "Configurar cuentas e impuestos"],
    ],
  },
  {
    key: "erp",
    label: "Operación del negocio",
    permissions: [
      ["erp.overview.read", "Ver resumen"],
      ["erp.movements.read", "Ver movimientos"],
      ["erp.movements.create", "Crear movimientos generales"],
      ["erp.sales.create", "Registrar ventas"],
      ["erp.purchases.create", "Registrar compras"],
      ["erp.expenses.create", "Registrar gastos"],
      ["erp.internal_consumption.create", "Registrar consumos internos"],
      ["erp.stock.manage", "Gestionar inventario"],
      ["erp.collections.create", "Registrar cobranzas"],
      ["erp.payments.create", "Registrar pagos"],
      ["erp.movements.update", "Editar borradores"],
      ["erp.movements.confirm", "Confirmar movimientos"],
      ["erp.movements.void", "Anular movimientos"],
      ["erp.transactions.read", "Ver transacciones"],
      ["erp.stock.read", "Ver movimientos de inventario"],
    ],
  },
  {
    key: "accounting",
    label: "Contabilidad e impuestos",
    permissions: [
      ["erp.ledger.read", "Ver libro y cuentas"],
      ["erp.ledger.manage", "Administrar cuentas"],
      ["erp.tax_profiles.read", "Ver impuestos"],
      ["erp.tax_profiles.manage", "Administrar impuestos"],
      ["financial.totals.read", "Ver totales financieros"],
      ["financial.costs.read", "Ver costos financieros"],
      ["financial.costs.manage", "Gestionar costos financieros"],
    ],
  },
  {
    key: "cash",
    label: "Cajas y arqueos",
    permissions: [
      ["cash.registers.read", "Ver cajas"],
      ["cash.registers.manage", "Crear y editar cajas"],
      ["cash.sessions.read", "Ver sesiones"],
      ["cash.sessions.open", "Abrir caja"],
      ["cash.sessions.close_own", "Cerrar caja propia"],
      ["cash.sessions.close_any", "Cerrar cualquier caja"],
      ["cash.sessions.review", "Revisar cierres"],
      ["cash.movements.read", "Ver movimientos de caja"],
      ["cash.operations.create", "Crear operaciones generales"],
      ["cash.operations.in", "Registrar ingresos"],
      ["cash.operations.out", "Registrar egresos"],
      ["cash.expected.read", "Ver saldo esperado"],
    ],
  },
]);

const ALL_PROJECT_PERMISSIONS = Object.freeze(
  [...new Set(PERMISSION_GROUPS.flatMap((group) =>
    (group.permissions || []).map(([permission]) => permission),
  ))],
);

export function projectRoleLabel(role) {
  return PROJECT_ROLE_LABELS[String(role || "viewer").toLowerCase()] || role || "Sin rol";
}

export function normalizeProjectAccess(access = {}) {
  const source = access && typeof access === "object" ? access : {};
  const role = String(source.role || "viewer").toLowerCase();
  const superAdmin = role === "superadmin"
    || source.isSuperAdmin === true
    || source.globalRole === "superadmin";
  const resolved = superAdmin
    || Boolean(source.role)
    || Array.isArray(source.permissions)
    || Boolean(source.scopes);

  return {
    resolved,
    status: String(source.status || "active").toLowerCase(),
    role: superAdmin ? "superadmin" : role,
    permissions: superAdmin
      ? [...ALL_PROJECT_PERMISSIONS]
      : (Array.isArray(source.permissions) ? source.permissions : []),
    scopes: {
      cashRegisterIds: superAdmin
        ? []
        : (Array.isArray(source.scopes?.cashRegisterIds)
          ? source.scopes.cashRegisterIds.map(String)
          : []),
      branchCodes: superAdmin
        ? []
        : (Array.isArray(source.scopes?.branchCodes)
          ? source.scopes.branchCodes.map(String)
          : []),
      ownOperationsOnly: superAdmin ? false : source.scopes?.ownOperationsOnly === true,
      financialVisibility: superAdmin
        ? "full"
        : (source.scopes?.financialVisibility || "operational"),
    },
    blindCashClosing: superAdmin ? false : source.blindCashClosing === true,
  };
}

export function isProjectAccessActive(access) {
  const normalized = normalizeProjectAccess(access);
  return normalized.role === "superadmin" || normalized.status === "active";
}

export function hasProjectPermission(access, permission) {
  const normalized = normalizeProjectAccess(access);
  return isProjectAccessActive(normalized) && normalized.permissions.includes(permission);
}

export function hasAnyProjectPermission(access, permissions = []) {
  const normalized = normalizeProjectAccess(access);
  if (!isProjectAccessActive(normalized)) return false;
  const granted = new Set(normalized.permissions);
  return permissions.some((permission) => granted.has(permission));
}


export function hasProjectRole(access, roles = []) {
  const normalized = normalizeProjectAccess(access);
  if (!normalized.resolved || !isProjectAccessActive(normalized)) return false;
  return roles.map((role) => String(role || "").toLowerCase()).includes(normalized.role);
}


export function effectivePermissionsForMember(member, accessCatalog = {}) {
  const preset = accessCatalog?.rolePresets?.[member?.role] || [];
  const granted = Array.isArray(member?.permissions) ? member.permissions : [];
  const denied = new Set(Array.isArray(member?.deniedPermissions) ? member.deniedPermissions : []);
  return [...new Set([...preset, ...granted])].filter((permission) => !denied.has(permission));
}
